import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import DataTable from "../../../Tables/DataTable/DataTable";
import { COLUMN_TYPES } from "../../../../constants/common";
import ApiServices from "../../../../services/ApiServices";
import { CONTENT, CATALOG, BUNDLE } from "../../../../constants/api-constants";
import {
  contentTypeOptions,
  typeOfContentOptions,
} from "../../../Distributors/constants";
import { useLoading } from "../../../../hooks/useLoader";
import ViewItemsDialog from "./ViewItemsDialog";
import ContentPreviewDialog from "./ContentPreviewDialog";
import "./SelectContentDialog.css";

/* Tab identifiers shared with the test and the nested dialogs. */
export const TAB_KEYS = {
  CONTENT: "content",
  CATALOG: "catalog",
  BUNDLE: "bundle",
};

/* Page-size selector options exposed by the DataTable. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50];

const MAX_SELECTION = 10;

const DEFAULT_FILTER = {
  lineOfBusinessList: [],
  typeOfFormList: [],
  selectedColumns: [],
  contentType: [],
  status: [1],
  publishStatus: [1],
  catalogType: [],
  bundleType: [],
  contentAssociationStatus: [],
  selectedContentOriginators: [],
  isShareableViaReusableLink: null,
  enterpriseGroupIds: [],
};

const DEFAULT_QUERY = {
  searchText: "",
  SortType: "desc",
  SortColumn: "isFavorite",
  PageNumber: 1,
  PageSize: PAGE_SIZE_OPTIONS[0],
  filter: DEFAULT_FILTER,
};

/* Per-tab definitions — the `endpoint` mirrors the URL pattern used by
 * ContentList / CatalogList / BundleList:
 *   `${API}/${distributorId}/0/${encodeURIComponent(JSON.stringify(query))}`
 *
 * `columns` matches the shape consumed by the shared DataTable component
 * and uses field names available on the records returned by these APIs.
 * `showView` adds an ACTION column (View icon) for Catalog/Bundle only. */
const TAB_DEFS = {
  [TAB_KEYS.CONTENT]: {
    label: "Content",
    endpoint: CONTENT,
    showView: true,
    columns: [
      {
        fieldName: "formNumber",
        label: "Form Number",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 120,
      },
      {
        fieldName: "latestVersion",
        label: "Edition Date",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 120,
      },
      {
        fieldName: "name",
        label: "Content Name",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 180,
      },
      {
        fieldName: "typeOfContentName",
        label: "Type of Content",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 130,
      },
      {
        fieldName: "lineOfBusinessList",
        label: "LOB",
        isSelected: true,
        type: COLUMN_TYPES.MULTI_VALUE,
        width: 140,
      },
      {
        fieldName: "countryName",
        label: "Country",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 100,
      },
      {
        fieldName: "states",
        label: "State/Province",
        isSelected: true,
        type: COLUMN_TYPES.MULTI_VALUE,
        width: 140,
      },
    ],
  },
  [TAB_KEYS.CATALOG]: {
    label: "Catalog",
    endpoint: CATALOG,
    showView: true,
    columns: [
      {
        fieldName: "name",
        label: "Catalog Name",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 200,
      },
      {
        fieldName: "latestVersion",
        label: "Edition Date",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 120,
      },
      {
        fieldName: "lineOfBusinessList",
        label: "LOB",
        isSelected: true,
        type: COLUMN_TYPES.MULTI_VALUE,
        width: 140,
      },
      {
        fieldName: "countryName",
        label: "Country",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 100,
      },
      {
        fieldName: "states",
        label: "State/Province",
        isSelected: true,
        type: COLUMN_TYPES.MULTI_VALUE,
        width: 140,
      },
    ],
  },
  [TAB_KEYS.BUNDLE]: {
    label: "Bundle",
    endpoint: BUNDLE,
    showView: true,
    columns: [
      {
        fieldName: "name",
        label: "Bundle Name",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 200,
      },
      {
        fieldName: "latestVersion",
        label: "Edition Date",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 120,
      },
      {
        fieldName: "lineOfBusinessList",
        label: "LOB",
        isSelected: true,
        type: COLUMN_TYPES.MULTI_VALUE,
        width: 140,
      },
      {
        fieldName: "countryName",
        label: "Country",
        isSelected: true,
        type: COLUMN_TYPES.BASIC,
        sort: true,
        width: 100,
      },
      {
        fieldName: "states",
        label: "State/Province",
        isSelected: true,
        type: COLUMN_TYPES.MULTI_VALUE,
        width: 140,
      },
    ],
  },
};

const TAB_ORDER = [TAB_KEYS.CONTENT, TAB_KEYS.CATALOG, TAB_KEYS.BUNDLE];

/** Append an ACTION column so DataTable renders the View icon for Catalog/Bundle. */
const buildColumns = (tabKey) => {
  const def = TAB_DEFS[tabKey];
  if (!def.showView) return def.columns;
  return [
    ...def.columns,
    {
      fieldName: "actions",
      label: "View",
      isSelected: true,
      type: COLUMN_TYPES.ACTION,
      width: 80,
    },
  ];
};

const emptySelectionMap = () => ({
  [TAB_KEYS.CONTENT]: {},
  [TAB_KEYS.CATALOG]: {},
  [TAB_KEYS.BUNDLE]: {},
});

const countSelections = (map) =>
  Object.values(map).reduce((sum, group) => sum + Object.keys(group).length, 0);

/* Entries seeded from edit-mode hydration may arrive without a `tab`
 * (the GET ad API only returns numeric ids). Track those by id so we
 * can (a) include them in the selection count, (b) flatten them back
 * into the submit payload even if the user never opens the matching
 * tab, and (c) auto-promote them into the right tab once a fetched
 * record's id matches. */
const countUnresolved = (unresolved) => Object.keys(unresolved).length;

/* Same enrichment the reference list components apply so the table shows
 * human-readable Type / Content Type labels (records only carry the raw
 * numeric codes). */
const enrichRecord = (record) => ({
  ...record,
  typeOfContentName:
    typeOfContentOptions.find((opt) => opt.value === record.typeOfContent)
      ?.name || "",
  contentTypeName:
    contentTypeOptions.find((opt) => opt.value === record.contentType)?.name ||
    "",
});

const emptyTabState = () => ({
  records: [],
  totalPages: 0,
});

/* Build the next selectionMap with newly-matched rows promoted into `tabKey`.
 * Pure helper — extracted to keep the fetch effect's nesting shallow. */
const promoteMatchedIntoTab = (curr, tabKey, matched) => {
  const nextTab = { ...curr[tabKey] };
  matched.forEach((row) => {
    if (nextTab[row.contentId]) return;
    nextTab[row.contentId] = {
      tab: tabKey,
      id: row.contentId,
      name: row.name || row.formNumber || `Item ${row.contentId}`,
      data: row,
    };
  });
  return { ...curr, [tabKey]: nextTab };
};

/* Build the next unresolved map with all matched ids removed. Pure helper. */
const removeMatchedFromUnresolved = (prev, matched) => {
  const next = { ...prev };
  matched.forEach((row) => {
    delete next[row.contentId];
  });
  return next;
};

/* Promote any unresolved ids whose record just appeared in `tabKey`.
 * Encapsulates both state updates so the fetch effect only makes one call
 * (keeps nesting depth shallow for SonarQube). */
const promoteResolvedRecords = (
  records,
  tabKey,
  setSelectionMap,
  setUnresolved,
) => {
  setUnresolved((prev) => {
    if (countUnresolved(prev) === 0) return prev;
    const matched = records.filter((r) => prev[r.contentId]);
    if (matched.length === 0) return prev;
    setSelectionMap((curr) => promoteMatchedIntoTab(curr, tabKey, matched));
    return removeMatchedFromUnresolved(prev, matched);
  });
};

/**
 * SelectContentDialog
 * Modal for the "Link Specific Content" radio option. Renders three tabs
 * (Content / Catalog / Bundle), each backed by the shared DataTable
 * component. Selections are capped at MAX_SELECTION across all tabs and
 * Catalog/Bundle rows expose a View icon that opens a read-only nested
 * popup (ViewItemsDialog) listing the items they contain.
 *
 * Data source: live Content/Catalog/Bundle endpoints, mirroring the same
 * URL contract used by ContentList / CatalogList / BundleList —
 *   `${API}/${distributorId}/0/${encodeURIComponent(JSON.stringify(query))}`
 * Server-side pagination/search; pass `distributorId` from the parent.
 */
const SelectContentDialog = ({
  open,
  initialSelection,
  onClose,
  onSubmit,
  distributorId,
}) => {
  const { showLoader } = useLoading();
  const [activeTab, setActiveTab] = useState(TAB_KEYS.CONTENT);
  const [selectionMap, setSelectionMap] = useState(emptySelectionMap());
  /* Map of id -> stub entry for items whose tab wasn't provided in
   * initialSelection (edit-mode hydration). Resolved into selectionMap
   * as records load. */
  const [unresolved, setUnresolved] = useState({});
  const [queryByTab, setQueryByTab] = useState({
    [TAB_KEYS.CONTENT]: { ...DEFAULT_QUERY },
    [TAB_KEYS.CATALOG]: { ...DEFAULT_QUERY },
    [TAB_KEYS.BUNDLE]: { ...DEFAULT_QUERY },
  });
  const [dataByTab, setDataByTab] = useState({
    [TAB_KEYS.CONTENT]: emptyTabState(),
    [TAB_KEYS.CATALOG]: emptyTabState(),
    [TAB_KEYS.BUNDLE]: emptyTabState(),
  });
  const [limitError, setLimitError] = useState("");
  const [viewItemsFor, setViewItemsFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  // Debounce id for the search input — matches the 1s debounce used by the
  // reference list components so we don't fire a request per keystroke.
  const searchDebounceRef = useRef(null);

  /* Re-seed local state every time the dialog opens. */
  useEffect(() => {
    if (!open) return;
    const seeded = emptySelectionMap();
    const stubs = {};
    (initialSelection || []).forEach((entry) => {
      if (!entry) return;
      const id = entry.id ?? entry.contentId;
      if (id == null) return;
      if (entry.tab && seeded[entry.tab]) {
        seeded[entry.tab][id] = { ...entry, id };
      } else {
        stubs[id] = { id, name: entry.name, data: entry.data };
      }
    });
    setSelectionMap(seeded);
    setUnresolved(stubs);
    setQueryByTab({
      [TAB_KEYS.CONTENT]: { ...DEFAULT_QUERY },
      [TAB_KEYS.CATALOG]: { ...DEFAULT_QUERY },
      [TAB_KEYS.BUNDLE]: { ...DEFAULT_QUERY },
    });
    setDataByTab({
      [TAB_KEYS.CONTENT]: emptyTabState(),
      [TAB_KEYS.CATALOG]: emptyTabState(),
      [TAB_KEYS.BUNDLE]: emptyTabState(),
    });
    setActiveTab(TAB_KEYS.CONTENT);
    setLimitError("");
    setViewItemsFor(null);
    setPreviewFor(null);
  }, [open, initialSelection]);

  /* Fetch records for the active tab whenever its query (page, search, sort)
   * changes. Same URL contract as the reference list components.
   * `distributorId` is required — fall back to a no-op while it's missing
   * so we don't fire a malformed request. */
  useEffect(() => {
    if (!open) return;
    // distributorId must be a positive number/string — 0 / null / "" produce
    // a 404 (`/content/0/0/...` is not a valid route).
    const distId = Number(distributorId);
    if (!distId || Number.isNaN(distId) || distId <= 0) return;

    const tabKey = activeTab;
    const def = TAB_DEFS[tabKey];
    const query = queryByTab[tabKey];
    let cancelled = false;

    const fetchRecords = async () => {
      try {
        showLoader(true);
        const response = await ApiServices.httpGet(
          `${def.endpoint}/${distId}/0/${encodeURIComponent(
            JSON.stringify(query),
          )}`,
        );
        if (cancelled) return;
        const records = (response?.result?.records || []).map(enrichRecord);
        /* Promote any unresolved ids whose record appears in this tab. */
        promoteResolvedRecords(records, tabKey, setSelectionMap, setUnresolved);
        setDataByTab((prev) => ({
          ...prev,
          [tabKey]: {
            records,
            totalPages: response?.result?.totalPages || 0,
          },
        }));
      } catch (err) {
        if (!cancelled) toast.error("Internal server error");
      } finally {
        if (!cancelled) showLoader(false);
      }
    };

    fetchRecords();
    return () => {
      cancelled = true;
    };
  }, [open, activeTab, queryByTab, distributorId, showLoader]);

  /* Clear any pending search debounce when the dialog closes. */
  useEffect(() => {
    if (open) return;
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, [open]);

  const totalSelected = useMemo(
    () => countSelections(selectionMap) + countUnresolved(unresolved),
    [selectionMap, unresolved],
  );

  const tabDef = TAB_DEFS[activeTab];
  const query = queryByTab[activeTab];
  const { records, totalPages } = dataByTab[activeTab];

  /* Decorate visible rows with `isChecked` from the selection map so DataTable
   * checkboxes reflect persistent selection across pagination/tab switches. */
  const adminTable = useMemo(
    () =>
      records.map((row) => ({
        ...row,
        isChecked: Boolean(selectionMap[activeTab][row.contentId]),
      })),
    [records, selectionMap, activeTab],
  );

  const updateQuery = useCallback(
    (patch) => {
      setQueryByTab((prev) => ({
        ...prev,
        [activeTab]: { ...prev[activeTab], ...patch },
      }));
    },
    [activeTab],
  );

  /* ── DataTable handlers ───────────────────────────────────────────── */

  const handleChangePage = useCallback(
    (_event, newPage) => {
      updateQuery({ PageNumber: newPage });
    },
    [updateQuery],
  );

  const handlePageSizeChange = useCallback(
    (size) => {
      updateQuery({ PageSize: Number(size), PageNumber: 1 });
    },
    [updateQuery],
  );

  const handleSearch = useCallback(
    (event) => {
      // Debounce to match the reference list components (1s pause).
      const value = event.target.value;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        updateQuery({ searchText: value, PageNumber: 1 });
      }, 1000);
    },
    [updateQuery],
  );

  const handleSort = useCallback(
    (sortOrder, sortBy) => {
      setQueryByTab((prev) => {
        const current = prev[activeTab];
        let nextSortType = "asc";
        if (current.SortColumn === sortBy) {
          nextSortType = sortOrder === "asc" ? "desc" : "asc";
        }
        return {
          ...prev,
          [activeTab]: {
            ...current,
            SortColumn: sortBy,
            SortType: nextSortType,
          },
        };
      });
    },
    [activeTab],
  );

  const handleCheckboxChange = useCallback(
    (event, value) => {
      const { checked } = event.target;
      setSelectionMap((prev) => {
        const next = {
          ...prev,
          [activeTab]: { ...prev[activeTab] },
        };

        const addRow = (row, draft) => {
          if (draft[activeTab][row.contentId]) return true;
          if (countSelections(draft) >= MAX_SELECTION) return false;
          draft[activeTab][row.contentId] = {
            tab: activeTab,
            id: row.contentId,
            name: row.name || row.formNumber || `Item ${row.contentId}`,
            data: row,
          };
          return true;
        };

        if (value === "all") {
          if (checked) {
            let blocked = false;
            adminTable.forEach((row) => {
              const added = addRow(row, next);
              if (!added) blocked = true;
            });
            setLimitError(
              blocked
                ? `Limit reached: Max ${MAX_SELECTION} items allowed.`
                : "",
            );
          } else {
            adminTable.forEach((row) => {
              delete next[activeTab][row.contentId];
            });
            setLimitError("");
          }
          return next;
        }

        // Single row toggle.
        if (checked) {
          const row = adminTable.find((r) => r.contentId === value);
          if (!row) return prev;
          const added = addRow(row, next);
          if (!added) {
            setLimitError(`Limit reached: Max ${MAX_SELECTION} items allowed.`);
            return prev;
          }
          setLimitError("");
        } else {
          delete next[activeTab][value];
          setLimitError("");
        }
        return next;
      });
    },
    [activeTab, adminTable],
  );

  /* View icon → Content tab opens the Content Preview popup (mirrors the
   * AddEditContent.js Content Preview tab); Catalog/Bundle open the
   * read-only nested items popup. */
  const handleView = useCallback(
    (row) => {
      if (activeTab === TAB_KEYS.CONTENT) {
        setPreviewFor({ contentId: row.contentId, name: row.name });
        return;
      }
      setViewItemsFor({ type: activeTab, item: row });
    },
    [activeTab],
  );

  const handleViewClose = useCallback(() => setViewItemsFor(null), []);

  const handlePreviewClose = useCallback(() => setPreviewFor(null), []);

  const handleAdd = useCallback(() => {
    const flat = [];
    Object.values(selectionMap).forEach((group) => {
      Object.values(group).forEach((entry) => flat.push(entry));
    });
    /* Preserve any still-unresolved stubs so edit-mode ids aren't dropped
     * just because the user never opened the matching tab. */
    Object.values(unresolved).forEach((entry) => flat.push(entry));
    onSubmit(flat);
    onClose();
  }, [selectionMap, unresolved, onSubmit, onClose]);

  const handleTabChange = useCallback((_, val) => {
    setActiveTab(val);
    setLimitError("");
  }, []);

  const columns = useMemo(() => buildColumns(activeTab), [activeTab]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="select-content-dialog"
      aria-labelledby="select-content-dialog-title"
      maxWidth="lg"
    >
      <DialogTitle
        id="select-content-dialog-title"
        className="select-content-dialog__title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Select Items</span>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="select-content-dialog__content">
        <Tabs value={activeTab} onChange={handleTabChange} className="sc-tabs">
          {TAB_ORDER.map((key) => {
            const def = TAB_DEFS[key];
            const count = Object.keys(selectionMap[key]).length;
            return (
              <Tab
                key={key}
                value={key}
                label={count > 0 ? `${def.label} (${count})` : def.label}
              />
            );
          })}
        </Tabs>

        {limitError && <div className="sc-limit-error">{limitError}</div>}

        <div className="sc-datatable-wrap">
          <DataTable
            key={activeTab}
            uniqueKey="contentId"
            columns={columns}
            adminTable={adminTable}
            query={query}
            page={query.PageNumber}
            totalPages={totalPages}
            pageSize={query.PageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            handleChangePage={handleChangePage}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            onSort={handleSort}
            onCheckboxChange={handleCheckboxChange}
            showView={tabDef.showView}
            onView={handleView}
            permissions={[]}
          />
        </div>

        <div className="sc-selected-summary">
          {totalSelected} of {MAX_SELECTION} selected
          <span className="content-redirect-required"> *</span>
        </div>
      </DialogContent>

      <DialogActions className="select-content-dialog__actions">
        <Button variant="outlined" onClick={onClose} className="sc-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          className="sc-btn sc-btn--primary"
          disabled={totalSelected === 0}
        >
          + Add
        </Button>
      </DialogActions>

      <ViewItemsDialog
        open={Boolean(viewItemsFor)}
        type={viewItemsFor?.type}
        item={viewItemsFor?.item}
        distributorId={distributorId}
        onClose={handleViewClose}
      />

      <ContentPreviewDialog
        open={Boolean(previewFor)}
        contentId={previewFor?.contentId}
        contentName={previewFor?.name}
        onClose={handlePreviewClose}
      />
    </Dialog>
  );
};

SelectContentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialSelection: PropTypes.arrayOf(
    PropTypes.shape({
      tab: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      data: PropTypes.object,
    }),
  ),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  distributorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SelectContentDialog.defaultProps = {
  initialSelection: [],
  distributorId: null,
};

export default memo(SelectContentDialog);
